import { QueryKey, UseMutationResult } from "@tanstack/react-query"
import {
  CorrespondenceFormValues,
  MetaData,
  MTTRoles,
  OrganizationRoles,
  usePaginatedTableWithUrlState,
} from "toptaal-shared"
import { CorrespondenceType } from "./correspondencePermissions"

export type UseCorrespondencePaginatedQuery = ReturnType<
  typeof usePaginatedTableWithUrlState<CorrespondenceFiltersResponse>
>

type CreateCorrespondenceThreadMutation = UseMutationResult<
  CreateCorrespondenceResponse,
  Error,
  CreateCorrespondenceWithFilesData
>

type ReplyCorrespondenceThreadMutation = UseMutationResult<
  RespondToCorrespondenceResponse,
  Error,
  RespondToCorrespondenceRequest
>

type ArchiveAction = "archive" | "unarchive"

export const correspondenceEntityTypesMap = [
  "Student",
  "Project",
  "LessonGroup",
] as const satisfies readonly CorrespondenceEntityType[]

export const correspondenceEntityTypesTranslations = {
  Student: "Cursist",
  Project: "Contract",
  LessonGroup: "Groep",
} as const satisfies Record<CorrespondenceEntityType, string>

type CorrespondenceEntityId = number

type CorrespondenceEntityType = "Student" | "Project" | "LessonGroup"
type CorrespondenceEntityName = string

type CorrespondenceApiPrefix = "students" | "projects" | "lesson_groups"

type CorrespondenceContextType =
  | CorrespondenceEntityType
  | "inbox"
  | "student-inbox"

export const entityTypeToApiPrefix: Record<
  CorrespondenceEntityType,
  CorrespondenceApiPrefix
> = {
  Student: "students",
  Project: "projects",
  LessonGroup: "lesson_groups",
}

export type CorrespondenceAboutOption = Array<{ value: string; label: string }>
export type CorrespondenceRoles = OrganizationRoles[] | MTTRoles[] | ["student"]

interface CorrespondenceContextValue {
  createCorrespondenceThreadMutation: CreateCorrespondenceThreadMutation
  entityType: CorrespondenceEntityType | null
  entityId: CorrespondenceEntityId | null
  entityName?: CorrespondenceEntityName

  handleNavigateToThread: (thread: CorrespondenceThread) => void
  contextType?: CorrespondenceContextType

  isCorrespondenceInbox?: boolean
  isStudentInbox?: boolean

  canCreateCorrespondence: boolean
  correspondenceCreatePermissions?: {
    groupCorrespondence: boolean
    projectCorrespondence: boolean
    studentCorrespondence: boolean
  }

  entityOptions?: {
    students?: CorrespondenceAboutOption
    projects?: CorrespondenceAboutOption
    groups?: CorrespondenceAboutOption
  }

  loadEntityOptions?: (
    type: CorrespondenceEntityType,
  ) => Promise<CorrespondenceAboutOption>

  correspondencePermission: {
    root: "mtt" | "org" | "student"
    roles: CorrespondenceRoles
    baseUserId: number
  }
}

interface CorrespondenceMutationParams {
  entityType: CorrespondenceEntityType | null
  entityId: CorrespondenceEntityId | null
  queryKey: QueryKey
}

interface CorrespondenceQueryParams extends CorrespondenceMutationParams {
  threadId: CorrespondenceThread["id"]
}

interface CorrespondenceParticipantsQueryParams extends Omit<
  CorrespondenceMutationParams,
  "queryKey"
> {
  type: CorrespondenceType | null
}

// Correspondence types
interface Author {
  id: number
  name: string
  email: string
}

interface Attachment {
  id: number
  filename: string
  insertedAt: string
  updatedAt: string
  filepath: string
}

interface Correspondence {
  author: Author
  content: string
  correspondenceThreadId: number
  id: number
  insertedAt: string
  updatedAt: string
  attachments: Attachment[]
}

interface CorrespondenceThread {
  correspondences: Correspondence[]
  hasUnread: boolean
  id: number
  insertedAt: string
  subject: string
  subjectName: string
  unreadCount: number
  updatedAt: string
  entityType: CorrespondenceEntityType
  entityId: CorrespondenceEntityId
  type: CorrespondenceType

  participants: CorrespondenceParticipant[]
  visibleForTeachers: boolean
  visibleForOrganizationUsers: boolean
}

export type CorrespondenceMessagesType =
  CorrespondenceMessages["data"]["correspondences"][number][]

interface CorrespondenceMessages {
  data: {
    correspondences: Correspondence[]
  }
  meta: MetaData
}

interface CreateCorrespondencePayload {
  data: {
    subject: string
    message: string
  }
}

// API types
interface CorrespondenceFiltersResponse {
  data: {
    correspondenceThreads: CorrespondenceThread[]
    totalUnreadCount: number
  }
  meta: MetaData
}

interface CreateCorrespondenceResponse {
  data: CorrespondenceThread
}

interface RespondToCorrespondenceRequest {
  correspondence: {
    content: string
  }
  files?: File[]
  entityId?: CorrespondenceEntityId
  entityType?: CorrespondenceEntityType
}

interface RespondToCorrespondenceResponse {
  data: Correspondence
}

type GetCorrespondenceResponse = {
  data: CorrespondenceThread
}

type CorrespondenceDocumentParams = {
  correspondenceThreadId: number
  correspondenceId: number
  displayFilename: string
}

type CorrespondenceDownloadHandler = (
  correspondenceId: number,
  attachmentId: number,
  attachmentName: string,
) => void

interface CorrespondenceFileUploadOptions {
  files: File[]
  entityId: CorrespondenceEntityId
  entityType: CorrespondenceEntityType
  correspondenceThreadId: number
  correspondenceId: number
}

interface UploadUrlResponse {
  data: {
    url: string
  }
}

interface CreateCorrespondenceWithFilesData extends CorrespondenceFormValues {
  files?: File[]
}

interface CorrespondenceParticipant {
  id: number
  name: string
  email: string
  type: "ToptaalUser" | "OrganizationUser" | "Student" | "LessonGroup"
  function?: string // user function/title
  roles?: MTTRoles[] // mtt specific
  organizationName?: string // org user specific
}

export type {
  Correspondence,
  CreateCorrespondenceWithFilesData,
  CorrespondenceThread,
  CorrespondenceMessages,
  CorrespondenceFiltersResponse,
  CorrespondenceContextValue,
  CorrespondenceEntityType,
  CorrespondenceApiPrefix,
  CorrespondenceEntityId,
  CorrespondenceEntityName,
  CorrespondenceContextType,
  CorrespondenceQueryParams,
  CorrespondenceParticipantsQueryParams,
  CorrespondenceMutationParams,
  CreateCorrespondenceResponse,
  CreateCorrespondencePayload,
  CreateCorrespondenceThreadMutation,
  ReplyCorrespondenceThreadMutation,
  RespondToCorrespondenceRequest,
  RespondToCorrespondenceResponse,
  GetCorrespondenceResponse,
  CorrespondenceDocumentParams,
  CorrespondenceDownloadHandler,
  CorrespondenceFileUploadOptions,
  CorrespondenceParticipant,
  Attachment,
  UploadUrlResponse,
  ArchiveAction,
}
